import { lazy } from 'react';

/**
 * Enhanced lazy loading with automatic retry logic
 * Handles ChunkLoadError which often occurs during deployment or server restarts
 * 
 * @param {Function} componentImport - Function that returns a dynamic import()
 * @param {number} retriesLeft - Number of retry attempts remaining
 * @param {number} interval - Interval between retries in ms
 * @returns {React.Component} Lazy-loaded component with retry logic
 */
export const lazyRetry = (componentImport, retriesLeft = 3, interval = 1000) => {
  return lazy(() =>
    new Promise((resolve, reject) => {
      componentImport()
        .then(resolve)
        .catch((error) => {
          setTimeout(() => {
            if (retriesLeft <= 0) {
              reject(error);
              return;
            }
            lazyRetry(componentImport, retriesLeft - 1, interval)._payload._result().then(resolve, reject);
          }, interval);
        });
    })
  );
};

// Simplified version for easier use
export const robustLazy = (componentImport) => {
  return lazy(async () => {
    const pageHasAlreadyBeenReloaded = window.localStorage.getItem('page-has-been-reloaded');
    
    try {
      const component = await componentImport();
      window.localStorage.removeItem('page-has-been-reloaded');
      return component;
    } catch (error) {
      if (!pageHasAlreadyBeenReloaded) {
        // First failure: reload the page to get fresh assets
        window.localStorage.setItem('page-has-been-reloaded', 'true');
        window.location.reload();
      }
      
      // If we already reloaded and it still fails, bubble up the error
      throw error;
    }
  });
};
